export function calculateSavings(individualTotalCents, offerPriceCents) {
  const total = Number(individualTotalCents);
  const price = Number(offerPriceCents);

  if (!Number.isSafeInteger(total) || !Number.isSafeInteger(price) || total <= 0 || price < 0 || price >= total) {
    return null;
  }

  const amountCents = total - price;
  return {
    amountCents,
    percent: Math.round((amountCents / total) * 1000) / 10,
  };
}

export function formatMoney(cents, currency, locale) {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents) / 100);
  } catch {
    return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
  }
}

export function formatPercent(percent, locale) {
  try {
    return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(percent / 100);
  } catch {
    return `${percent}%`;
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('zeya-offer-selector')) {
  class ZeyaOfferSelector extends HTMLElement {
    #section;
    #form;
    #variantInput;
    #priceElement;
    #variantPicker;
    #variantPickerWasHidden;
    #acceleratedCheckout;
    #pickup;
    #pickupWasHidden;
    #sticky;
    #selectedTitle;
    #selectedPrice;
    #selectedSavings;
    #liveRegion;

    connectedCallback() {
      this.#section = this.closest('.shopify-section');
      this.#form = this.#section?.querySelector('product-form-component');
      this.#variantInput = this.#form?.querySelector('input[name="id"]');
      if (!this.#section || !this.#form || !this.#variantInput) return;

      this.#priceElement = this.#section.querySelector('product-price');
      this.#variantPicker = this.#section.querySelector('variant-picker');
      this.#variantPickerWasHidden = this.#variantPicker?.hidden ?? false;
      this.#acceleratedCheckout = this.#form.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
      this.#pickup = this.#section.querySelector('local-pickup');
      this.#pickupWasHidden = this.#pickup?.hidden ?? false;
      this.#sticky = this.#section.querySelector('sticky-add-to-cart');
      this.#selectedTitle = this.querySelector('[data-selected-title]');
      this.#selectedPrice = this.querySelector('[data-selected-price]');
      this.#selectedSavings = this.querySelector('[data-selected-savings]');
      this.#liveRegion = this.querySelector('[data-live-region]');

      this.addEventListener('change', this.#handleChange);
      this.#section.addEventListener('variant:update', this.#handleVariantUpdate);

      this.#updateSavings();
      this.hidden = false;

      const selectedOffer = this.querySelector('input[data-offer]:checked');
      if (selectedOffer) this.#applySelected(selectedOffer, false);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.#handleChange);
      this.#section?.removeEventListener('variant:update', this.#handleVariantUpdate);
    }

    #handleChange = (event) => {
      const offer = event.target;
      if (!(offer instanceof HTMLInputElement) || !offer.matches('[data-offer]') || !offer.checked) return;
      this.#applySelected(offer, true);
    };

    #handleVariantUpdate = (event) => {
      const { resource: variant, data } = event.detail ?? {};
      if (!data || data.newProduct || data.productId !== this.dataset.currentProductId) return;

      const currentOffer = this.querySelector('input[data-offer][data-is-current-product="true"]');
      if (!currentOffer) return;

      if (variant) {
        currentOffer.dataset.variantId = String(variant.id ?? '');
        currentOffer.value = String(variant.id ?? '');
        currentOffer.dataset.available = String(variant.available === true);
        currentOffer.dataset.variantTitle = variant.title ?? '';

        if (Number.isSafeInteger(Number(variant.price))) {
          currentOffer.dataset.priceCents = String(variant.price);
          currentOffer.dataset.priceText = formatMoney(variant.price, this.dataset.currency, document.documentElement.lang);
          const priceElement = currentOffer.closest('label')?.querySelector('[data-offer-price]');
          if (priceElement) priceElement.textContent = currentOffer.dataset.priceText;
        }

        const imageUrl = variant.featured_media?.preview_image?.src;
        if (imageUrl) currentOffer.dataset.imageUrl = imageUrl;
      } else {
        currentOffer.dataset.available = 'false';
      }

      currentOffer.disabled = currentOffer.dataset.available !== 'true';
      this.#updateSavings();
      if (currentOffer.checked) this.#applySelected(currentOffer, false);
    };

    #updateSavings() {
      const locale = document.documentElement.lang || 'fr';
      const template = this.dataset.savingsTemplate ?? '';

      this.querySelectorAll('input[data-offer]').forEach((offer) => {
        const savingsElement = offer.closest('label')?.querySelector('[data-savings]');
        const savings = calculateSavings(offer.dataset.individualTotalCents, offer.dataset.priceCents);
        if (!savingsElement || !savings) {
          if (savingsElement) {
            savingsElement.hidden = true;
            savingsElement.textContent = '';
          }
          return;
        }

        const amount = formatMoney(savings.amountCents, this.dataset.currency, locale);
        const savingsText = template.replace('__AMOUNT__', amount);
        savingsElement.textContent = `${savingsText} (${formatPercent(savings.percent, locale)})`;
        savingsElement.hidden = false;
      });
    }

    #applySelected(offer, announce) {
      const variantId = offer.dataset.variantId;
      const productId = offer.dataset.productId;
      if (!variantId || !productId) return;

      const available = offer.dataset.available === 'true';
      const isCurrentProduct = offer.dataset.isCurrentProduct === 'true';
      const title = offer.dataset.productTitle ?? '';
      const priceText = offer.dataset.priceText || formatMoney(offer.dataset.priceCents, this.dataset.currency, document.documentElement.lang);
      const savingsText = offer.closest('label')?.querySelector('[data-savings]')?.textContent ?? '';

      this.#variantInput.value = variantId;
      this.#form.dataset.productId = productId;
      this.#form.dataset.productUrl = offer.dataset.productUrl ?? '';
      if (this.#priceElement) this.#priceElement.hidden = !isCurrentProduct;
      if (this.#variantPicker) this.#variantPicker.hidden = isCurrentProduct ? this.#variantPickerWasHidden : true;
      if (this.#acceleratedCheckout) this.#acceleratedCheckout.hidden = !isCurrentProduct || !available;
      if (this.#pickup) this.#pickup.hidden = !isCurrentProduct || this.#pickupWasHidden;

      const addToCartContainer = this.#form.querySelector('add-to-cart-component');
      const addToCartButton = addToCartContainer?.querySelector('[ref="addToCartButton"]');
      this.#setButtonState(addToCartContainer, addToCartButton, available);
      if (offer.dataset.imageUrl) addToCartContainer?.setAttribute('data-product-variant-media', offer.dataset.imageUrl);

      if (this.#selectedTitle) this.#selectedTitle.textContent = title;
      if (this.#selectedPrice) this.#selectedPrice.textContent = priceText;
      if (this.#selectedSavings) {
        this.#selectedSavings.textContent = savingsText;
        this.#selectedSavings.hidden = !savingsText;
      }

      if (this.#sticky) {
        this.#sticky.dataset.productId = productId;
        this.#sticky.dataset.currentVariantId = variantId;
        this.#sticky.dataset.variantAvailable = String(available);
        const stickyTitle = this.#sticky.querySelector('[data-testid="sticky-product-title"]');
        const stickyPrice = this.#sticky.querySelector('[data-testid="sticky-price-display"]');
        const stickyVariant = this.#sticky.querySelector('[data-testid="sticky-variant-title"]');
        const stickyInfo = this.#sticky.querySelector('.sticky-add-to-cart__info');
        const stickyImage = this.#sticky.querySelector('[data-testid="sticky-product-image"]');
        const stickyImageWrapper = stickyImage?.closest('.sticky-add-to-cart__image');
        const stickyButton = this.#sticky.querySelector('[ref="addToCartButton"]');

        if (stickyTitle) stickyTitle.textContent = title;
        if (stickyPrice) stickyPrice.textContent = priceText;
        if (stickyVariant) {
          const hasDefaultVariant = offer.dataset.hasDefaultVariant === 'true';
          stickyVariant.textContent = hasDefaultVariant ? '' : (offer.dataset.variantTitle ?? '');
          stickyVariant.style.display = hasDefaultVariant ? 'none' : '';
        }
        if (stickyInfo) {
          stickyInfo.dataset.singleton = offer.dataset.hasDefaultVariant ?? 'true';
          stickyInfo.dataset.singleOption = offer.dataset.singleOption ?? 'false';
          stickyInfo.dataset.hasImage = String(Boolean(offer.dataset.imageUrl));
        }
        if (stickyImage && offer.dataset.imageUrl) {
          stickyImage.src = offer.dataset.imageUrl;
          stickyImage.alt = title;
          if (stickyImageWrapper) stickyImageWrapper.hidden = false;
        } else if (stickyImageWrapper) {
          stickyImageWrapper.hidden = true;
        }
        this.#setButtonState(this.#sticky, stickyButton, available);
      }

      if (announce && this.#liveRegion) {
        this.#liveRegion.textContent = [title, priceText, savingsText].filter(Boolean).join('. ');
      }
    }

    #setButtonState(container, button, available) {
      if (available) container?.enable?.();
      else container?.disable?.();
      if (!button) return;

      button.disabled = !available;
      const label = button.querySelector('.zeya-btn-label') || button.querySelector('.add-to-cart-text__content > span');
      if (label) label.textContent = available ? this.dataset.addToCartText : this.dataset.soldOutText;
    }
  }

  customElements.define('zeya-offer-selector', ZeyaOfferSelector);
}
